import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Indentification from "../../../elements/Indentification";
import { initialsValue } from "../../../../helpers";
import { postDetails } from "../../../../services/Admin/postService";

const SkeletonDetails = () => (
  <div className="animate-pulse">
    {/* Banner Skeleton */}
    <div className="h-64 bg-slate-200" />
    <div className="max-w-7xl mx-auto px-6 -mt-20 relative z-10 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl p-6 shadow">
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-3" />
            <div className="h-5 bg-slate-100 rounded w-1/4 mb-6" />
            <div className="flex gap-2 mb-8 flex-wrap">
              <div className="h-8 bg-slate-100 rounded-full w-32" />
              <div className="h-8 bg-slate-100 rounded-full w-28" />
              <div className="h-8 bg-slate-100 rounded-full w-24" />
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-4/5" />
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-slate-200 h-72" />
        </div>
      </div>
    </div>
  </div>
);

const JobDetailsPage = () => {
  const { id } = useParams();

  const { data: job, isLoading } = useQuery({
    queryKey: ["standard-job", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await postDetails({ id });

      if (res?.status !== 200) {
        toast.warning(res?.message || "Failed to fetch details");
        return null;
      }
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) return <SkeletonDetails />;

  if (!job)
    return (
      <div className="p-20 text-center font-sans text-slate-600">
        Job not found.
      </div>
    );

  const { content, organization, job: jobMeta, locationType, createdAt } = job;
  const enContent = content?.en;

  return (
    <div className="min-h-screen">
      {/* Banner Section */}
      {/* <div className="h-64 bg-gradient-to-r from-[#5B9BD5] to-[#4A8BC2] relative overflow-hidden">
        {job.coverMedia?.url && (
          <img
            src={job.coverMedia.url}
            className="w-full h-full object-cover opacity-20"
            alt="Job Banner"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10"></div>
      </div> */}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto sm:px-6 relative z-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header Card */}
            <div className="bg-white rounded-xl p-6 shadow">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-slate-800 leading-snug mb-2">
                    {enContent?.title}
                  </h1>
                  <div className="flex items-center gap-2 text-denim font-medium text-base">
                    <i className="fa-solid fa-building text-sm"></i>
                    <span>{organization?.content?.en?.title}</span>
                  </div>
                </div>
                <Indentification
                  image={organization?.logo?.url}
                  initial={initialsValue(organization?.content?.en?.title)}
                  size="xl"
                  type="square"
                  className="shadow-sm border border-slate-200"
                />
              </div>

              {/* Job Meta Tags */}
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 bg-purple-50 px-2 h-8 rounded-full border border-purple-200 text-purple-600 text-sm font-medium">
                  <i className="fa-solid fa-briefcase text-xs"></i>
                  <span className="capitalize">{jobMeta?.jobType}</span>
                </div>
                <div className="flex items-center gap-2 bg-amber-100 px-2 h-8 rounded-full border border-amber-200 text-amber-600 text-sm font-medium">
                  <i className="fa-solid fa-location-dot text-xs"></i>
                  <span className="capitalize">{locationType}</span>
                </div>
                <div className="flex items-center gap-2 bg-emerald-100 px-2 h-8 rounded-full border border-emerald-200 text-emerald-600 text-sm font-medium">
                  <i className="fa-solid fa-clock text-xs"></i>
                  <span>{new Date(createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Job Description Card */}
            <div className="bg-white rounded-xl p-6 shadow">
              <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <i className="fa-solid fa-file-lines text-denim"></i>
                Job Description
              </h3>
              <div
                className="text-slate-600 leading-relaxed prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: enContent?.content }}
              />
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Job Details Card */}
            <div className="bg-white rounded-xl p-6 shadow sticky">
              <h4 className="font-bold text-lg mb-5 text-slate-800 flex items-center gap-2">
                <i className="fa-solid fa-circle-info text-denim"></i>
                Job Details
              </h4>

              <div className="space-y-4">
                <div className="pb-4 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                    Salary Range
                  </p>
                  <p className="text-slate-800 font-bold text-lg flex items-center gap-2">
                    <i className="fa-solid fa-dollar-sign text-green-600 text-base"></i>
                    {jobMeta?.budget?.content?.en?.title || "Not specified"}
                  </p>
                </div>

                <div className="pb-4 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                    Experience Level
                  </p>
                  <p className="text-slate-800 font-bold text-lg flex items-center gap-2">
                    <i className="fa-solid fa-user-tie text-orange-600 text-base"></i>
                    {jobMeta?.experience?.content?.en?.title || "Not specified"}
                  </p>
                </div>

                <div className="pb-4 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                    Location Type
                  </p>
                  <p className="text-slate-800 font-bold text-lg flex items-center gap-2 capitalize">
                    <i className="fa-solid fa-map-marker-alt text-blue-600 text-base"></i>
                    {locationType}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                    Posted Date
                  </p>
                  <p className="text-slate-800 font-bold text-lg flex items-center gap-2">
                    <i className="fa-solid fa-calendar text-purple-600 text-base"></i>
                    {new Date(createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Company Info Card */}
            <div className="bg-white rounded-xl p-6 shadow">
              <h4 className="font-bold text-lg mb-3 flex items-center gap-2 text-slate-800">
                <i className="fa-solid fa-building text-denim"></i>
                About Company
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed">
                {organization?.content?.en?.content ||
                  "No company description available."}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;
